/**
 * useBackButtonClose — Android hardware back button support for overlay/modal views.
 *
 * When `isOpen` becomes true, pushes a history entry so the Android system back
 * button has something to pop. When the browser fires a `popstate` event (back
 * button pressed), calls `onClose`. When the overlay is dismissed via an in-app
 * button while the pushed entry is still on the stack, calls `window.history.back()`
 * to keep history consistent.
 *
 * Guarantees:
 * - Only one history entry is pushed per open (no double-push).
 * - Cleanup removes the popstate listener on unmount or when `isOpen` turns false.
 * - Works correctly when the user navigates between routes while the overlay is open.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  incrementCleanupBack,
  incrementActiveOverlays,
  decrementActiveOverlays,
} from '@/utils/back-button-coordinator';

/**
 * @param isOpen  Whether the overlay is currently visible.
 * @param onClose Callback to close the overlay (called by back button).
 * @param key     Unique string identifying this overlay (e.g. 'ringtone-full').
 * @returns       `{ markNavigating }` — call `markNavigating()` before an
 *                intentional route navigation so the cleanup does NOT call
 *                `history.back()` on unmount (which would undo the navigation).
 */
export function useBackButtonClose(
  isOpen: boolean,
  onClose: () => void,
  key: string,
): { markNavigating: () => void } {
  // Tracks whether WE pushed a history entry for this open session.
  const pushedRef = useRef(false);

  // Keep a stable ref to the latest onClose so the effect never needs to
  // re-run (and trigger cleanup) just because the caller passed a new
  // function reference — a very common source of spurious history.back() calls.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Track active overlay count in the coordinator so the global explore guard
  // knows not to navigate to Explore while an overlay is handling back presses.
  useEffect(() => {
    if (isOpen) {
      incrementActiveOverlays();
      return () => {
        decrementActiveOverlays();
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Push a sentinel entry so the back button has something to pop.
      window.history.pushState({ overlay: key }, '');
      pushedRef.current = true;

      const handlePopState = (e: PopStateEvent) => {
        // Only respond if the state doesn't match our sentinel
        // (i.e. the user popped past our entry).
        const state = e.state as Record<string, unknown> | null;
        if (state?.overlay === key) {
          // Still on our entry — shouldn't normally happen, but ignore.
          return;
        }
        // The sentinel was popped — close the overlay.
        pushedRef.current = false;
        onCloseRef.current();
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        // If the overlay was closed programmatically (not via back button),
        // the pushed entry is still on the stack — go back to clean it up.
        // Exception: if markNavigating() was called before an intentional route
        // navigation, pushedRef is already false so we skip history.back() to
        // avoid popping the newly-pushed navigation entry.
        if (pushedRef.current) {
          pushedRef.current = false;
          // Signal the global guard (and any other popstate listeners) that the
          // resulting popstate event is from our cleanup, not a real back press.
          incrementCleanupBack();
          window.history.back();
        }
      };
    }
    // When isOpen is false, nothing to set up.
    // NOTE: onClose intentionally omitted — stored in onCloseRef above so
    // a new function reference never triggers cleanup/re-setup.
  }, [isOpen, key]);

  // markNavigating: call before an intentional route navigation to prevent
  // the cleanup from calling history.back() on unmount.
  const markNavigating = useCallback(() => {
    pushedRef.current = false;
  }, []);

  return { markNavigating };
}
